import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { BadBucketPanelService } from '../../components/dlg-bad-bucket-position/bad-bucket-panel.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BatchNeighbors, PoState, poStateTranslation, ProductionBatch, Scan, ScanStatus } from '../../../model';
import { ProductionBatchState } from '../../../store/batch/batch.state';
import { ModelState } from '../../../store/model/model.state';
import { EMPTY, switchMap, tap } from 'rxjs';
import { fallbackImage } from 'src/app/components';
import { PoSplitDialogComponent } from '../../components/po-split-dialog/po-split-dialog.component';
import data from 'src/assets/baseMap.json';
import { Location } from '@angular/common';
import { AuthenticationState } from '../../../store/auth/auth.state';
import { UserRole } from "src/model";
import { BackendService } from '../../../services/backend';
import { isBadBucketScan } from '../../components/failure-reason/failure-reason-messages';

@Component({
  selector: 'app-po',
  templateUrl: './po.component.html',
  styleUrls: ['./po.component.scss'],
  standalone: false,
})
export class PoComponent {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public state: ProductionBatchState,
    private ref: ChangeDetectorRef,
    private location: Location,
    public authState: AuthenticationState,
    private backend: BackendService,
    public modelState: ModelState,
    private badBucketPanel: BadBucketPanelService,
  ) { }

  data: any = data;
  PoState = PoState;
  ScanStatus = ScanStatus;
  UserRole = UserRole;

  poStateTranslation = poStateTranslation;

  fallbackImage = fallbackImage;

  focusedScanId: string | null = null;
  scan: Scan | undefined = undefined;
  // Tab index: 0 = Images, 1 = Analysis. Deep links might map differently if we had more.
  // Let's align: 0 (Images), 1 (Analysis).
  // Note: existing scan details logic used index 1 for something else?
  // Checking original code: "if (tabData === 'details') { this.scanTabIndex = 1; }"
  // The user request says "first tab called Images and second Analysis".
  // So likely: 0=Images, 1=Analysis.
  // But wait, there was existing tab logic for "details".
  // Let's assume the user wants two MAIN tabs: Images vs Analysis.
  // The "details" view for a scan likely lives INSIDE the Images tab or is a separate view.
  // The user said: "make sure that every tab has its own deep link".
  // I will use `mainTabIndex` for Images/Analysis.
  // I will keep `scanTabIndex` if it was used for something inside Images (like Scan List vs Scan Details?),
  // BUT looking at the original code, `scanTabIndex` seemed to toggle between list and details view?
  // Original: `onScanTabChange`... `tabIndex === 1 ? base + '/details'`.
  // If I wrap everything in `nz-tabset`, I need to be careful.
  // Proposal:
  // Outer Tabset: Images (0), Analysis (1).
  // Inside Images: The existing logic (List <-> Details) or just List if Details is a modal/sidebar.
  // Let's look at `po.component.html` in next step to understand `scanTabIndex` usage.
  // For now, I'll add `mainTabIndex` and handle the route data.

  mainTabIndex: number = 0;
  scanTabIndex: number = 0;
  batchComment: string = '';

  @ViewChild('splitDialog')
  splitPoDialog!: PoSplitDialogComponent;

  @ViewChild('truncateDialog')
  truncatePoDialog!: PoSplitDialogComponent;

  showStatsScanId: string | null = null;

  showStatsAll: boolean = false;
  filterToMaxXdE: boolean = false;

  /** Batches immediately before/after the focused one by inspectionSequence (physical line order, not PO). */
  neighbors: BatchNeighbors | null = null;

  public get batch() {
    return this.state.focused;
  }

  /** Template helper: returns true for bad-bucket scans (DL-011 exact-match). */
  protected isBadBucket(scan: Scan): boolean {
    return isBadBucketScan(scan);
  }

  public get activeModel() {
    return this.modelState.all.find((m) => m.isActive) ?? null;
  }

  /** The latest scan in the focused batch, by sequenceInBatch. */
  public get lastScan(): Scan | undefined {
    const scans = this.batch?.scans;
    if (!scans || scans.length === 0) return undefined;
    return scans.reduce((latest, s) => (s.sequenceInBatch > latest.sequenceInBatch ? s : latest));
  }

  /**
   * True when the last scan's confidence indicator (CI) exceeds the CI threshold
   * (maxCi4ReliablePrediction) of the model that inspected that scan — i.e. the
   * prediction was unreliable. Mirrors the backend bypass gate (Inspector.cs, strict >).
   * Any missing piece (no scan, no ci, no model, no threshold) → false (no warning).
   */
  public get lastScanLowConfidence(): boolean {
    const scan = this.lastScan;
    if (!scan || scan.ci == null || scan.inspectionModelId == null) return false;
    const model = this.modelState.all.find((m) => m.id === scan.inspectionModelId);
    if (!model || model.maxCi4ReliablePrediction == null) return false;
    return scan.ci > model.maxCi4ReliablePrediction;
  }

  public scans(b: ProductionBatch) {
    return b.scans
      ?.filter((s) => (this.filterToMaxXdE ? b.dEPairMaxIds.includes(s.sequenceInBatch) : true))
      ?.slice()
      ?.reverse();
  }

  public openSplitDialog() {
    if (this.authState.hasAnyRole(UserRole.Operator, UserRole.Admin)) {
      this.splitPoDialog.show();
    }
  }

  modelHandle(id: string) {
    this.location.go(this.router.url + '/scans/' + id);
    this.scan = undefined;
    this.ref.detectChanges();
    this.scan = this.batch?.scans?.find((s) => s.id == id);

    const scan = this.scan;
    if (scan && isBadBucketScan(scan) && scan.state === ScanStatus.Failed) {
      this.badBucketPanel.open({ scan, batch: this.batch, isInViewMode: true }, (_scan) => this.closeScanDialog());
    }
  }

  ngOnInit() {
    // Read tab from route data to support deep linking to specific tabs
    // Read tab from route data to support deep linking to specific tabs
    const tabData = this.route.snapshot.data['tab'];
    if (tabData === 'details') {
      this.scanTabIndex = 1;
      this.mainTabIndex = 0; // Details are part of Images context usually
    } else if (tabData === 'analysis') {
      this.mainTabIndex = 1;
    } else {
      this.mainTabIndex = 0;
    }

    this.route.paramMap
      .pipe(
        switchMap((params) => {
          let poId = params.get('poId');
          return poId
            ? this.state.focusOn(poId).pipe(
              tap((p) => {
                this.batchComment = p.comment ?? '';
                let scanId = params.get('scanId');
                if (scanId && scanId != this.scan?.id) {
                  this.scan = undefined;
                  this.ref.detectChanges();
                  this.scan = p.scans?.find((s) => s.id == scanId);
                }

                this.neighbors = null;
                this.state.getNeighbors(p.id).subscribe((n) => {
                  this.neighbors = n;
                  this.ref.detectChanges();
                });
              }),
            )
            : EMPTY;
        }),
      )
      .subscribe();
  }

  back() {
    this.router.navigate(['/']);
  }

  closeScanDialog() {
    this.scan = undefined;
    this.scanTabIndex = 0;
    this.location.go('/pos/' + this.batch?.id);
  }

  onScanTabChange(tabIndex: number) {
    this.scanTabIndex = tabIndex;
    if (this.scan) {
      const base = '/pos/' + this.batch?.id + '/scans/' + this.scan.id;
      this.location.go(tabIndex === 1 ? base + '/details' : base);
    }
  }

  onMainTabChange(index: number) {
    this.mainTabIndex = index;
    if (index === 1) {
      // Analysis
      this.location.go('/pos/' + this.batch?.id + '/analysis');
    } else {
      // Images
      this.location.go('/pos/' + this.batch?.id);
    }
  }

  split(quantity: number) {
    this.state.split(this.batch!.id, quantity);
  }

  truncate(quantity: number) {
    this.state.truncate(this.batch!.id, quantity);
  }

  saveBatchComment(batch: ProductionBatch) {
    this.backend.updateBatchComment(batch.id, this.batchComment).subscribe();
  }

  updateScanStatus(scan: Scan | undefined, status: ScanStatus) {
    this.state.updateStatus(scan!, status);
  }

  attach([subject, to]: [string, ProductionBatch]) {
    this.state.attach(subject, to.id);
  }

  protected readonly Math = Math;

  navScans(dir: number) {
    // @ts-ignore
    let newId = this.batch?.scans?.find((s) => s.sequenceInBatch == this.scan.sequenceInBatch + dir)?.id;
    if (newId) this.modelHandle(newId);
  }

  hoverStarted(s: Scan) {
    this.showStatsScanId = s.id;
    // const content = document.querySelector(`#hover${s.id}`);
    // // @ts-ignore
    // content.style.display = "flex";
  }

  hoverEnded(_s: Scan) {
    this.showStatsScanId = null;
    //
    // const content = document.querySelector(`#hover${s.id}`);
    // // @ts-ignore
    // content.style.display = "none";
  }

  reInspect(scan: Scan | undefined) {
    this.backend.newInspection(scan!.productionBatchId, scan!.id).subscribe((s) => {
      this.scan = s;
      s.productionBatch = this.batch ?? undefined;
      this.ref.detectChanges();
    });
  }

  preInspect(scan: Scan | undefined) {
    this.backend.getInspection(scan!.productionBatchId, scan!.id).subscribe((s) => {
      this.scan = s;
      s.productionBatch = this.batch ?? undefined;
      this.ref.detectChanges();
    });
  }
}
